import { useEffect, useRef, useState } from 'react';
import { ApiClientError, documentsApi } from '../lib/api';
import { UPLOAD_CONCURRENCY, mapWithConcurrency } from '../lib/concurrency';
import {
  bakeRotation,
  collectDroppedPictures,
  collectPictureFiles,
  convertToJpeg,
  saveBlob,
  toJpegName,
  zipFiles,
} from '../lib/photos';
import { WorkOrderPicker } from '../components/WorkOrderPicker';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Spinner,
} from '../components/ui';
import { IconPhoto, IconRotate, IconTrash } from '../components/icons';

/**
 * Photo Manager — import photos of any type (HEIC/HEIF, JPG, PNG, …), a whole
 * folder, or a .zip of photos; each is converted to JPEG in the browser. Preview
 * the results, rotate as needed, select which to keep, then download the selected
 * ones as a ZIP and/or upload them to a Corrigo work order as Picture attachments.
 *
 * Conversion and rotation are 100% client-side; only the upload path touches the
 * server, reusing the existing document-upload endpoint. Download works with no
 * Corrigo connection at all.
 */

type ConvertStatus = 'converting' | 'ready' | 'error';
type UploadStatus = 'idle' | 'uploading' | 'done' | 'error';

interface Photo {
  id: string;
  /** The JPEG output name (source name with a .jpg extension). */
  name: string;
  /** Converted JPEG at rotation 0; undefined until conversion resolves. */
  jpeg?: File;
  /** Object URL for the preview (of `jpeg`); revoked on removal/unmount. */
  url?: string;
  /** 0 / 90 / 180 / 270 — applied to the bytes at download/upload time. */
  rotation: number;
  selected: boolean;
  convert: ConvertStatus;
  convertError?: string;
  upload: UploadStatus;
  uploadProgress: number;
  uploadError?: string;
  attachmentId?: string;
}

export function PhotoManager() {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<{ ok: number; failed: number; woNumber: string } | null>(null);

  // Work-order resolution for the upload path (mirrors the Documents flow).
  const [woNumber, setWoNumber] = useState('');
  const [picking, setPicking] = useState(false);
  const [resolved, setResolved] = useState<{ workOrderId: number; woNumber: string } | null>(null);
  const [woError, setWoError] = useState<string | null>(null);

  const idSeq = useRef(0);

  useEffect(() => {
    documentsApi
      .status()
      .then((s) => setConfigured(s.configured))
      .catch(() => setConfigured(false));
  }, []);

  // Revoke every preview URL when the page unmounts (ref always holds latest).
  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(
    () => () => {
      photosRef.current.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    },
    [],
  );

  const update = (id: string, patch: Partial<Photo>) =>
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const readyPhotos = photos.filter((p) => p.convert === 'ready');
  const selectedReady = readyPhotos.filter((p) => p.selected);
  const converting = photos.some((p) => p.convert === 'converting');

  // ── Import ────────────────────────────────────────────────────────────────

  /** Stage a batch of already-filtered image Files and convert each to JPEG. */
  const addPictureFiles = async (files: File[]) => {
    if (files.length === 0) return;
    const staged = files.map((file) => ({ id: `p${idSeq.current++}`, file }));
    setPhotos((prev) => [
      ...prev,
      ...staged.map(({ id, file }) => ({
        id,
        name: toJpegName(file.name),
        rotation: 0,
        selected: true,
        convert: 'converting' as ConvertStatus,
        upload: 'idle' as UploadStatus,
        uploadProgress: 0,
      })),
    ]);
    await Promise.allSettled(
      staged.map(async ({ id, file }) => {
        try {
          const jpeg = await convertToJpeg(file);
          update(id, { jpeg, url: URL.createObjectURL(jpeg), convert: 'ready' });
        } catch (err) {
          update(id, {
            convert: 'error',
            selected: false,
            convertError: err instanceof Error ? err.message : 'Could not convert this file.',
          });
        }
      }),
    );
  };

  const handleDrop = async (dt: DataTransfer) => {
    setImporting(true);
    try {
      await addPictureFiles(await collectDroppedPictures(dt));
    } finally {
      setImporting(false);
    }
  };

  const handleInputFiles = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    setImporting(true);
    try {
      await addPictureFiles(await collectPictureFiles(Array.from(fileList)));
    } finally {
      setImporting(false);
    }
  };

  // ── Per-photo actions ───────────────────────────────────────────────────────

  const rotate = (id: string) =>
    setPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, rotation: (p.rotation + 90) % 360 } : p)),
    );

  const toggle = (id: string) =>
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p)));

  const remove = (id: string) =>
    setPhotos((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone?.url) URL.revokeObjectURL(gone.url);
      return prev.filter((p) => p.id !== id);
    });

  const clearAll = () =>
    setPhotos((prev) => {
      prev.forEach((p) => p.url && URL.revokeObjectURL(p.url));
      return [];
    });

  const setAllSelected = (selected: boolean) =>
    setPhotos((prev) => prev.map((p) => (p.convert === 'ready' ? { ...p, selected } : p)));

  // ── Download ─────────────────────────────────────────────────────────────────

  const downloadOne = async (p: Photo) => {
    if (!p.jpeg) return;
    const file = await bakeRotation(p.jpeg, p.rotation);
    saveBlob(file, file.name);
  };

  const downloadSelected = async () => {
    if (selectedReady.length === 0) return;
    setDownloading(true);
    try {
      const baked = await Promise.all(selectedReady.map((p) => bakeRotation(p.jpeg!, p.rotation)));
      saveBlob(await zipFiles(baked), 'photos.zip');
    } finally {
      setDownloading(false);
    }
  };

  // ── Upload to Corrigo ────────────────────────────────────────────────────────

  const lookup = () => {
    if (!woNumber.trim()) return;
    setResolved(null);
    setWoError(null);
    setPicking(true);
  };

  const uploadOne = async (p: Photo): Promise<boolean> => {
    if (!resolved || !p.jpeg) return false;
    update(p.id, { upload: 'uploading', uploadProgress: 0, uploadError: undefined });
    try {
      const file = await bakeRotation(p.jpeg, p.rotation);
      const result = await documentsApi.uploadGeneral(
        resolved.woNumber,
        file,
        { documentType: 'Picture', workOrderId: resolved.workOrderId },
        (frac) => update(p.id, { uploadProgress: frac }),
      );
      update(p.id, { upload: 'done', uploadProgress: 1, attachmentId: result.attachmentId });
      return true;
    } catch (err) {
      update(p.id, {
        upload: 'error',
        uploadError: err instanceof ApiClientError ? err.message : 'Upload failed.',
      });
      return false;
    }
  };

  const uploadSelected = async () => {
    if (!resolved) return;
    const targets = photos.filter(
      (p) => p.selected && p.convert === 'ready' && p.upload !== 'done' && p.jpeg,
    );
    if (targets.length === 0) return;
    setUploading(true);
    setUploadSummary(null);
    try {
      // Bounded pool: firing every upload at once saturates Corrigo's endpoint
      // and triggers server-side timeouts/retries on ~a quarter of them.
      const results = await mapWithConcurrency(targets, UPLOAD_CONCURRENCY, (p) => uploadOne(p));
      const ok = results.filter((r) => r.status === 'fulfilled' && r.value).length;
      setUploadSummary({ ok, failed: targets.length - ok, woNumber: resolved.woNumber });
    } finally {
      setUploading(false);
    }
  };

  const uploadableCount = selectedReady.filter((p) => p.upload !== 'done').length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        backTo="/"
        icon={<IconPhoto className="h-5 w-5" />}
        title="Photo Manager"
        subtitle="Import photos of any type — a folder or a .zip works too — convert them to JPG, rotate and pick the keepers, then download as a ZIP or upload to a Corrigo work order."
      />

      <ImportZone importing={importing} onDrop={handleDrop} onFiles={handleInputFiles} />

      {photos.length > 0 && (
        <Card>
          <CardHeader
            title="Photos"
            subtitle={`${readyPhotos.length} ready${converting ? ' · converting…' : ''} · ${selectedReady.length} selected`}
            action={
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setAllSelected(true)} disabled={readyPhotos.length === 0}>
                  Select all
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setAllSelected(false)} disabled={selectedReady.length === 0}>
                  Clear
                </Button>
                <Button variant="ghost" size="sm" onClick={clearAll}>
                  Remove all
                </Button>
              </div>
            }
          />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {photos.map((p) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  onToggle={() => toggle(p.id)}
                  onRotate={() => rotate(p.id)}
                  onRemove={() => remove(p.id)}
                  onDownload={() => void downloadOne(p)}
                />
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <p className="text-sm text-slate-500">
                {selectedReady.length === 0
                  ? 'Select photos to download or upload.'
                  : `${selectedReady.length} selected photo${selectedReady.length === 1 ? '' : 's'}.`}
              </p>
              <Button onClick={() => void downloadSelected()} disabled={downloading || selectedReady.length === 0}>
                {downloading ? <Spinner /> : `Download ${selectedReady.length || ''} as ZIP`}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {photos.length > 0 && (
        <Card>
          <CardHeader
            title="Upload to a Corrigo work order"
            subtitle="Selected photos upload as Picture attachments on the work order."
          />
          <CardBody className="space-y-4">
            {configured === false && (
              <Banner tone="warn">
                Corrigo is not configured, so photos can’t be uploaded yet — you can still download them.
                An admin must set the Corrigo credentials.
              </Banner>
            )}

            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Field label="Work order number">
                  <Input
                    value={woNumber}
                    onChange={(e) => {
                      setWoNumber(e.target.value);
                      setResolved(null);
                      setUploadSummary(null);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && lookup()}
                    placeholder="WO-100245"
                    disabled={configured === false}
                  />
                </Field>
              </div>
              <Button
                onClick={lookup}
                disabled={picking || !woNumber.trim() || configured === false}
              >
                {picking ? <Spinner /> : 'Look up'}
              </Button>
            </div>

            {woError && <Banner tone="error">{woError}</Banner>}

            {picking && (
              <WorkOrderPicker
                woNumber={woNumber.trim()}
                search={() => documentsApi.searchWorkOrders(woNumber.trim())}
                onResolved={(workOrderId) => {
                  setResolved({ workOrderId, woNumber: woNumber.trim() });
                  setPicking(false);
                }}
                onReset={() => setPicking(false)}
              />
            )}

            {resolved && (
              <>
                <Banner tone="success">
                  Work order <strong>{resolved.woNumber}</strong>{' '}
                  <span className="text-slate-400">#{resolved.workOrderId}</span> — ready for upload.
                </Banner>
                <div className="flex items-center justify-end">
                  <Button
                    onClick={() => void uploadSelected()}
                    disabled={uploading || uploadableCount === 0}
                  >
                    {uploading ? <Spinner /> : `Upload ${uploadableCount || ''} selected photo${uploadableCount === 1 ? '' : 's'}`}
                  </Button>
                </div>
              </>
            )}

            {uploadSummary && (
              <Banner tone={uploadSummary.failed > 0 ? 'warn' : 'success'}>
                {uploadSummary.ok > 0 && (
                  <>
                    ✓ Uploaded <strong>{uploadSummary.ok}</strong> photo
                    {uploadSummary.ok === 1 ? '' : 's'} to work order{' '}
                    <strong>{uploadSummary.woNumber}</strong>.
                  </>
                )}
                {uploadSummary.failed > 0 && (
                  <>
                    {' '}
                    {uploadSummary.failed} photo{uploadSummary.failed === 1 ? '' : 's'} failed to
                    upload — the affected {uploadSummary.failed === 1 ? 'photo is' : 'photos are'}{' '}
                    marked in red above. You can retry by uploading again.
                  </>
                )}
              </Banner>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ── Import dropzone ─────────────────────────────────────────────────────────────

function ImportZone({
  importing,
  onDrop,
  onFiles,
}: {
  importing: boolean;
  onDrop: (dt: DataTransfer) => void;
  onFiles: (files: FileList | null) => void;
}) {
  const [over, setOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  return (
    <Card>
      <CardBody>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            onDrop(e.dataTransfer);
          }}
          onClick={() => fileInput.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-10 text-center transition ${
            over ? 'border-accent-500 bg-accent-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400'
          }`}
        >
          <span className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-white text-accent-600 ring-1 ring-inset ring-accent-100">
            {importing ? <Spinner className="h-5 w-5" /> : <IconPhoto className="h-5 w-5" />}
          </span>
          <span className="text-sm font-medium text-slate-700">
            {importing ? 'Importing…' : 'Drop photos, a folder, or a .zip here'}
          </span>
          <span className="mt-1 text-xs text-slate-400">
            HEIC, HEIF, JPG, PNG and more · non-image files are ignored
          </span>
          <div className="mt-4 flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                fileInput.current?.click();
              }}
            >
              Browse files
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                folderInput.current?.click();
              }}
            >
              Browse folder
            </Button>
          </div>
        </div>

        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/*,.heic,.heif,.zip"
          className="hidden"
          onChange={(e) => {
            onFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={folderInput}
          type="file"
          multiple
          // @ts-expect-error — non-standard but widely supported directory picker.
          webkitdirectory=""
          directory=""
          className="hidden"
          onChange={(e) => {
            onFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </CardBody>
    </Card>
  );
}

// ── Photo card ──────────────────────────────────────────────────────────────────

function PhotoCard({
  photo,
  onToggle,
  onRotate,
  onRemove,
  onDownload,
}: {
  photo: Photo;
  onToggle: () => void;
  onRotate: () => void;
  onRemove: () => void;
  onDownload: () => void;
}) {
  const ready = photo.convert === 'ready';
  const busy = photo.upload === 'uploading';

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white transition ${
        photo.selected && ready ? 'border-accent-400 ring-1 ring-accent-200' : 'border-slate-200'
      }`}
    >
      <div className="relative flex aspect-square items-center justify-center bg-slate-100">
        {ready && photo.url ? (
          <img
            src={photo.url}
            alt={photo.name}
            className="h-full w-full object-contain transition-transform"
            style={{ transform: `rotate(${photo.rotation}deg)` }}
          />
        ) : photo.convert === 'converting' ? (
          <div className="flex flex-col items-center gap-2 text-slate-400">
            <Spinner className="h-5 w-5" />
            <span className="text-xs">Converting…</span>
          </div>
        ) : (
          <div className="px-3 text-center text-xs text-red-600">{photo.convertError}</div>
        )}

        {ready && (
          <label className="absolute left-2 top-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-white/90 shadow-sm ring-1 ring-slate-200">
            <input
              type="checkbox"
              checked={photo.selected}
              onChange={onToggle}
              className="h-4 w-4 accent-accent-600"
              aria-label={`Select ${photo.name}`}
            />
          </label>
        )}

        {photo.upload === 'done' && (
          <span className="absolute right-2 top-2">
            <Badge tone="green">uploaded</Badge>
          </span>
        )}

        {busy && (
          <div className="absolute inset-x-0 bottom-0 h-1.5 bg-slate-200">
            <div
              className="h-full bg-accent-500 transition-all"
              style={{ width: `${Math.round(photo.uploadProgress * 100)}%` }}
            />
          </div>
        )}
      </div>

      <div className="space-y-1 px-2.5 py-2">
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700" title={photo.name}>
            {photo.name}
          </span>
          {ready && (
            <>
              <IconButton title="Rotate 90°" onClick={onRotate} disabled={busy}>
                <IconRotate className="h-4 w-4" />
              </IconButton>
              <IconButton title="Download" onClick={onDownload} disabled={busy}>
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 3v12" />
                  <path d="m8 11 4 4 4-4" />
                  <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                </svg>
              </IconButton>
            </>
          )}
          <IconButton title="Remove" onClick={onRemove} disabled={busy}>
            <IconTrash className="h-4 w-4" />
          </IconButton>
        </div>
        {photo.upload === 'error' && <p className="text-xs text-red-600">{photo.uploadError}</p>}
      </div>
    </div>
  );
}

function IconButton({
  children,
  title,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"
    >
      {children}
    </button>
  );
}
